import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { matchAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { MessageCircle, Heart, Star } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';

export default function MatchesPage() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMatches();
    
    // Listen for new matches
    const handleWsMessage = (event) => {
      const data = event.detail;
      if (data.type === 'new_match') {
        loadMatches();
      }
    };
    window.addEventListener('ws-message', handleWsMessage);
    return () => window.removeEventListener('ws-message', handleWsMessage);
  }, []);

  const loadMatches = async () => {
    try {
      const response = await matchAPI.getAll();
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to load matches:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-24" data-testid="matches-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="px-6 py-4">
          <h1 className="font-heading text-2xl text-white">Matches</h1>
          <p className="text-sm text-gray-400 font-body">
            {matches.length} {matches.length === 1 ? 'match' : 'matches'}
          </p>
        </div>
      </header>

      {matches.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-dark-elevated flex items-center justify-center mb-4">
            <Heart className="w-10 h-10 text-gray-500" />
          </div>
          <h2 className="font-heading text-xl text-white mb-2">No Matches Yet</h2>
          <p className="text-gray-400 font-body mb-6">
            Keep swiping to find your perfect match!
          </p>
          <button
            onClick={() => navigate('/discover')}
            className="btn-gold px-6 py-3 rounded-full font-body font-semibold"
          >
            Start Discovering
          </button>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-3">
          {matches.map((match, index) => (
            <motion.div
              key={match.match_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/chat/${match.match_id}`)}
              className="flex items-center gap-4 p-4 bg-dark-paper border border-white/5 rounded-2xl hover:border-gold/30 transition-colors cursor-pointer"
              data-testid={`match-${match.match_id}`}
            >
              {/* Profile Photo */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gold/30">
                  <img
                    src={match.other_user.photo || 'https://via.placeholder.com/64'}
                    alt={match.other_user.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {match.is_super_match && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                    <Star className="w-3 h-3 text-black" fill="black" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-body font-semibold text-white truncate">
                    {match.other_user.name}
                  </h3>
                  {match.other_user.is_verified && (
                    <span className="text-gold text-xs">✓</span>
                  )}
                </div>
                
                {match.last_message ? (
                  <p className="text-sm text-gray-400 truncate">
                    {match.last_message.content}
                  </p>
                ) : (
                  <p className="text-sm text-gold">Start the conversation!</p>
                )}
                
                <p className="text-xs text-gray-500 mt-1">
                  Matched {formatDistanceToNow(new Date(match.matched_at))} ago
                </p>
              </div>

              {/* Action */}
              <div className="flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-gold" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
